import { match, P } from 'ts-pattern'

import { Button, type ButtonProps } from 'ui/Button/Button'
import type { VersionState } from 'features/versions/types'
import { PromiseButton } from 'ui/Button/PromiseButton'

type Props = {
  installVersion: () => Promise<unknown> | undefined
  uninstallVersion: () => Promise<void>
  cancelInstallation: () => void
  versionState: VersionState
}

export function VersionActionButton(props: Props) {
  const { versionState, installVersion, uninstallVersion, cancelInstallation } = props
  const progress: number | null = match(versionState)
    .with({ status: P.union('downloading', 'unpacking', 'installing') }, (_versionStatus) =>
      Math.round(_versionStatus.progress),
    )
    .with(
      {
        status: P.union(
          'notInstalled',
          'done',
          'installed',
          'partiallyInstalled',
          'error',
          'updateAvailable',
          'canceling',
          'cancelled',
        ),
      },
      () => null,
    )
    .exhaustive()

  // prettier-ignore
  const installButtonParams = match<typeof versionState, ButtonProps>(versionState)
    .with({ status: P.union('notInstalled', 'partiallyInstalled') }, () => ({
      children: 'Install',
      onClick: installVersion,
      color: 'light',
    }))
    .with({ status: 'updateAvailable' }, () => ({ children: 'Update', onClick: installVersion }))
    .with({ status: 'done' }, () => ({ children: <>✅ Installed</>, disabled: true, color: 'success', outline: true }))
    .with({ status: 'installed' }, () => ({
      children: <>✅ Installed</>,
      disabled: true,
      color: 'success',
      outline: true,
    }))
    .with({ status: 'error' }, () => ({ children: <>Error</>, disabled: true, color: 'danger', outline: true }))
    .with({ status: P.union('downloading', 'unpacking', 'installing') }, (state) => ({
      children: state.status,
      spinner: true,
      disabled: true,
      color: 'dark',
      outline: true,
    }))
    .with({ status: 'canceling' }, () => ({
      children: 'Canceling...',
      spinner: true,
      disabled: true,
      color: 'danger',
      outline: true,
    }))
    .with({ status: 'cancelled' }, () => ({
      children: 'Cancelled',
      disabled: true,
      color: 'danger',
      outline: true,
    }))
    .exhaustive()

  return (
    <>
      <Button {...installButtonParams} className="capitalize z-10 w-full opacity-100" />
      {versionState.status === 'installed' && (
        <PromiseButton color="danger" className="capitalize w-full mt-1" onClick={uninstallVersion}>
          Uninstall
        </PromiseButton>
      )}
      {['downloading', 'unpacking', 'installing'].includes(versionState.status) && (
        <Button color="danger" className="capitalize w-full mt-1" onClick={cancelInstallation} outline>
          Cancel
        </Button>
      )}
      {progress !== null && (
        <div>
          <div className="w-full bg-gray-100 rounded-full h-2.5">
            <div className="bg-gray-300 h-2.5 rounded-full" style={{ width: `${progress}%` }} />
          </div>
          <div className="text-center text-[10px] text-slate-400">{progress}%</div>
        </div>
      )}
    </>
  )
}
